import React, { useState, useEffect } from "react";
import { apiCall } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Phone, MessageSquare, Shield, Settings as SettingsIcon, Headphones, Download, Sparkles } from "lucide-react";

export default function SettingsPage() {
  const [settings, setSettings] = useState({ mask_mobile: false, bonvoice_caller_id: "", bonvoice_channel_id: "11", whatsapp_enabled: false, auto_dialer_enabled: false, admin_extension: "", admin_caller_id: "", admin_sip_server: "voice2.bonvoice.com", admin_sip_password: "", google_sheet_id: "", google_service_account_json: "", openai_enabled: false, openai_api_key: "", openai_model: "gpt-4o-mini", broadcast_retention_days: 7 });
  const [waConfig, setWaConfig] = useState({ api_url: "", api_version: "v19.0", access_token: "", phone_number_id: "", waba_id: "", business_id: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      apiCall("get", "/settings"),
      apiCall("get", "/whatsapp/config"),
      apiCall("get", "/default-messages")
    ]).then(([settingsRes, waRes, msgsRes]) => {
      const msgs = msgsRes.data.messages?.map(m => m.text) || [""];
      setSettings(prev => ({ ...prev, ...settingsRes.data, defaultMsgs: msgs.length ? msgs : [""] }));
      setWaConfig(prev => ({ ...prev, ...waRes.data }));
    }).catch(() => toast.error("Failed")).finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiCall("put", "/settings", settings);
      await apiCall("put", "/whatsapp/config", waConfig);
      toast.success("All settings saved");
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const handleRecategorize = async () => {
    try { const { data } = await apiCall("post", "/clients/recategorize"); toast.success(`Recategorized ${data.recategorized} clients`); } catch { toast.error("Failed"); }
  };

  const handleDownload = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/extension/download`, {
        headers
      });
      if (!response.ok) throw new Error("Download failed");
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "b2c-extension.zip");
      document.body.appendChild(link);
      link.click();
      
      // Delay cleanup so the browser has time to download the blob
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 5000);
      toast.success("Extension downloaded successfully");
    } catch (err) {
      toast.error("Failed to download extension");
      console.error(err);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <div>
        <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">Configure CRM preferences</p>
      </div>

      {/* Agent Settings */}
      <Card className="border border-border">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Shield className="w-4 h-4" /> Agent Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between"><div><p className="text-sm font-medium">Mask Mobile Numbers</p><p className="text-xs text-muted-foreground">Hide mobile from agents (MASKED)</p></div><Switch checked={settings.mask_mobile} onCheckedChange={v => setSettings({ ...settings, mask_mobile: v })} data-testid="mask-mobile-switch" /></div>
          <div className="flex items-center justify-between"><div><p className="text-sm font-medium">Auto Dialer</p><p className="text-xs text-muted-foreground">Enable auto dialing</p></div><Switch checked={settings.auto_dialer_enabled} onCheckedChange={v => setSettings({ ...settings, auto_dialer_enabled: v })} data-testid="auto-dialer-switch" /></div>
        </CardContent>
      </Card>

      {/* Admin Call Config */}
      <Card className="border border-border">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Headphones className="w-4 h-4" /> Admin Call Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs text-muted-foreground">Admin's own Click-to-Call config</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Extension</Label><Input value={settings.admin_extension} onChange={e => setSettings({ ...settings, admin_extension: e.target.value })} placeholder="e.g. 13955" className="mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Outbound Caller ID</Label><Input value={settings.admin_caller_id} onChange={e => setSettings({ ...settings, admin_caller_id: e.target.value })} placeholder="e.g. 8065295488" className="mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">SIP Server</Label><Input value={settings.admin_sip_server} onChange={e => setSettings({ ...settings, admin_sip_server: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">SIP Password</Label><Input type="password" value={settings.admin_sip_password} onChange={e => setSettings({ ...settings, admin_sip_password: e.target.value })} className="mt-1" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Bonvoice Config */}
      <Card className="border border-border">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Phone className="w-4 h-4" /> Bonvoice Configuration</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 rounded-md bg-muted">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Callback URL</p>
            <code className="text-sm font-mono text-primary break-all">{`${process.env.REACT_APP_BACKEND_URL}/api/bonvoice/call-log`}</code>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Bonvoice API Token</Label><Input value={settings.bonvoice_token || ""} onChange={e => setSettings({ ...settings, bonvoice_token: e.target.value })} placeholder="Paste token from Bonvoice" className="mt-1 font-mono text-xs" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Default Caller ID</Label><Input value={settings.bonvoice_caller_id} onChange={e => setSettings({ ...settings, bonvoice_caller_id: e.target.value })} placeholder="e.g. 4843518123" className="mt-1" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Channel ID</Label><Input value={settings.bonvoice_channel_id} onChange={e => setSettings({ ...settings, bonvoice_channel_id: e.target.value })} placeholder="11" className="mt-1" /></div>
          </div>
        </CardContent>
      </Card>

      {/* WhatsApp Config - Full Meta API */}
      <Card className="border border-border">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><MessageSquare className="w-4 h-4" /> WhatsApp Business API (Meta)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between"><div><p className="text-sm font-medium">Enable WhatsApp</p><p className="text-xs text-muted-foreground">When enabled, agents can send WhatsApp messages</p></div><Switch checked={settings.whatsapp_enabled} onCheckedChange={v => setSettings({ ...settings, whatsapp_enabled: v })} data-testid="whatsapp-enabled-switch" /></div>
          <Separator />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label className="text-xs uppercase tracking-wider text-muted-foreground">Access Token</Label><Input value={waConfig.access_token} onChange={e => setWaConfig({ ...waConfig, access_token: e.target.value })} placeholder="Paste token from merawaapi.com" className="mt-1 font-mono text-xs" data-testid="wa-access-token" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">API URL</Label><Input value={waConfig.api_url} onChange={e => setWaConfig({ ...waConfig, api_url: e.target.value })} placeholder="https://crmapi.1automations.com/api/meta" className="mt-1" data-testid="wa-api-url" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">API Version</Label><Input value={waConfig.api_version} onChange={e => setWaConfig({ ...waConfig, api_version: e.target.value })} placeholder="v19.0" className="mt-1" data-testid="wa-api-version" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone Number ID</Label><Input value={waConfig.phone_number_id} onChange={e => setWaConfig({ ...waConfig, phone_number_id: e.target.value })} placeholder="1148659041653435" className="mt-1" data-testid="wa-phone-id" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">WABA ID</Label><Input value={waConfig.waba_id} onChange={e => setWaConfig({ ...waConfig, waba_id: e.target.value })} placeholder="983310790766415" className="mt-1" data-testid="wa-waba-id" /></div>
            <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Business ID</Label><Input value={waConfig.business_id} onChange={e => setWaConfig({ ...waConfig, business_id: e.target.value })} placeholder="1223906165828576" className="mt-1" data-testid="wa-business-id" /></div>
          </div>
        </CardContent>
      </Card>

      {/* OpenAI / LLM Integration */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-500" /> OpenAI / LLM Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Enable OpenAI LLM</p>
              <p className="text-xs text-muted-foreground">Use AI to generate smart messages, call scripts, and automated responses</p>
            </div>
            <Switch
              checked={settings.openai_enabled || false}
              onCheckedChange={v => setSettings({ ...settings, openai_enabled: v })}
              data-testid="openai-enabled-switch"
            />
          </div>
          <Separator />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">OpenAI API Key</Label>
              <Input
                type="password"
                value={settings.openai_api_key || ""}
                onChange={e => setSettings({ ...settings, openai_api_key: e.target.value })}
                placeholder="sk-proj-..."
                className="mt-1 font-mono text-xs"
                data-testid="openai-api-key"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">AI Model</Label>
              <Select
                value={settings.openai_model || "gpt-4o-mini"}
                onValueChange={v => setSettings({ ...settings, openai_model: v })}
              >
                <SelectTrigger className="mt-1 h-9 text-xs">
                  <SelectValue placeholder="Select model" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gpt-4o-mini">gpt-4o-mini (Fast & Recommended)</SelectItem>
                  <SelectItem value="gpt-4o">gpt-4o (High Accuracy)</SelectItem>
                  <SelectItem value="gpt-4-turbo">gpt-4-turbo</SelectItem>
                  <SelectItem value="gpt-3.5-turbo">gpt-3.5-turbo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Google Sheets Configuration */}
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
            <Download className="w-4 h-4" /> Google Sheets Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Default Google Sheet ID or URL</Label>
            <Input 
              value={settings.google_sheet_id || ""} 
              onChange={e => setSettings({ ...settings, google_sheet_id: e.target.value })} 
              placeholder="e.g. 1BxiMVs0XRA5nFMdKv1aMoppt02IL5678" 
              className="mt-1" 
            />
            <p className="text-[10px] text-muted-foreground mt-1">Provide the spreadsheet ID or complete sharing URL.</p>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Google Service Account JSON Key</Label>
            <Textarea 
              value={settings.google_service_account_json || ""} 
              onChange={e => setSettings({ ...settings, google_service_account_json: e.target.value })} 
              placeholder="Paste your Service Account credentials JSON here" 
              className="mt-1 font-mono text-xs h-24" 
            />
            <p className="text-[10px] text-muted-foreground mt-1">Required for write (export) operations. Share your sheet with the service account email.</p>
          </div>
        </CardContent>
      </Card>

      {/* Chrome Extension Webhook */}
      <Card className="border border-border">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><SettingsIcon className="w-4 h-4" /> Chrome Extension Webhook</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 rounded-md bg-muted">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Webhook URL (paste in extension)</p>
            <code className="text-sm font-mono text-primary break-all select-all" data-testid="webhook-url">{`${process.env.REACT_APP_BACKEND_URL}/api/webhook/extension`}</code>
          </div>
          <div><Label className="text-xs uppercase tracking-wider text-muted-foreground">Webhook API Key (optional)</Label><Input value={settings.webhook_api_key || ""} onChange={e => setSettings({ ...settings, webhook_api_key: e.target.value })} placeholder="Set a key for security" className="mt-1 font-mono text-xs" /></div>
          <p className="text-xs text-muted-foreground">Supports events: test, client_created, user_fetched, client_logs_bulk, user_fetched_bulk</p>
          <div className="pt-2 border-t border-border flex flex-col gap-2">
            <Button 
              type="button"
              variant="outline" 
              onClick={handleDownload}
              className="w-full sm:w-auto flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4 mr-2 inline" />
              Download Chrome Extension (.zip)
            </Button>
            <p className="text-[10px] text-muted-foreground">The downloaded extension is pre-configured and always connected to this CRM instance.</p>
          </div>
        </CardContent>
      </Card>

      {/* System & Maintenance */}
      <Card className="border border-border">
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><SettingsIcon className="w-4 h-4" /> System &amp; Data Maintenance</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-border/60">
            <div>
              <p className="text-sm font-medium">Broadcast Reports Retention</p>
              <p className="text-xs text-muted-foreground">Auto-delete broadcast proofs and database records older than this threshold (0 = Never delete)</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                max="365"
                value={settings.broadcast_retention_days !== undefined ? settings.broadcast_retention_days : 7}
                onChange={e => setSettings({ ...settings, broadcast_retention_days: parseInt(e.target.value) || 0 })}
                className="w-24 h-8 text-xs font-mono text-center"
              />
              <span className="text-xs text-muted-foreground">Days</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <p className="text-sm font-medium">Auto-Recategorize All Clients</p>
              <p className="text-xs text-muted-foreground">Re-evaluate all clients into Fresh, Hot (0–2d), Regular (3–30d), and Cold (&gt;30d)</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRecategorize} className="text-xs h-8">
              <Sparkles className="w-3.5 h-3.5 mr-1 text-primary" /> Recategorize Now
            </Button>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto" data-testid="save-settings-btn"><Save className="w-4 h-4 mr-2" />{saving ? "Saving..." : "Save All Settings"}</Button>
    </div>
  );
}
